import React, { useState } from 'react';
import {
  Pill, CheckCircle2, Building2, HardHat, User, Users, ShieldCheck, Mail, Upload,
  ArrowRight, ArrowLeft, Landmark, Plus, Zap, FileCheck2, Rocket, Clock, DollarSign,
} from 'lucide-react';

const NAVY = '#0f2440';
const BLUE = '#2563eb';

const STEPS = [
  { key: 'welcome', label: 'Welcome' },
  { key: 'persona', label: 'Who you are' },
  { key: 'details', label: 'Your details' },
  { key: 'lenders', label: 'Your lenders' },
  { key: 'contractors', label: 'Contractors' },
  { key: 'compliance', label: 'W-9 & COI' },
  { key: 'done', label: 'All set' },
];

function BrandMark({ size = 40 }) {
  return <div className="flex items-center justify-center font-extrabold text-white" style={{ width: size, height: size, background: BLUE, borderRadius: size * 0.26, fontSize: size * 0.5 }}>D</div>;
}

function Why({ children }) {
  return (
    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 flex items-start gap-2">
      <Pill className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-emerald-800"><span className="font-semibold">The headache it removes:</span> {children}</p>
    </div>
  );
}

function Header({ eyebrow, title, sub }) {
  return (
    <div className="mb-5">
      {eyebrow && <div className="text-xs font-bold uppercase tracking-wider text-blue-600">{eyebrow}</div>}
      <h2 className="text-2xl font-extrabold text-slate-900 mt-1">{title}</h2>
      {sub && <p className="text-slate-500 text-sm mt-1">{sub}</p>}
    </div>
  );
}

const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-1';

export default function OnboardingAspirinPreview() {
  const [step, setStep] = useState(0);
  const [persona, setPersona] = useState('developer');
  const [lenders, setLenders] = useState([{ name: 'Ohnward Bank & Trust', email: 'constructiondept@ohnward.com' }]);
  const [lName, setLName] = useState('');
  const [lEmail, setLEmail] = useState('');
  const [contractors, setContractors] = useState([{ company: 'Suburban Lumber', email: 'ap@suburbanlumber.com' }]);
  const key = STEPS[step].key;
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <div className="text-center text-[11px] font-medium text-white py-1.5" style={{ background: '#b45309' }}>PREVIEW · Aspirin-focused onboarding concept — not your live flow</div>

      {/* top bar + stepper */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <BrandMark size={34} />
          <div className="font-bold text-slate-900">DrawReady</div>
          <div className="ml-auto text-xs text-slate-400">Step {step + 1} of {STEPS.length}</div>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-3">
          <div className="flex gap-1.5">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex-1 h-1.5 rounded-full" style={{ background: i <= step ? BLUE : '#e2e8f0' }} />
            ))}
          </div>
        </div>
      </div>

      <main className="flex-1 w-full">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            {key === 'welcome' && (
              <div className="text-center">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-xs font-semibold text-slate-600"><Pill className="w-4 h-4 text-rose-500" /> The aspirin for construction draws</span>
                <h1 className="mt-5 text-3xl sm:text-4xl font-extrabold text-slate-900">Construction draws are a headache.<br />Let's remove it.</h1>
                <p className="mt-3 text-slate-600 max-w-xl mx-auto">In about 3 minutes we'll set you up so every draw goes out complete, verified, and straight to your bank — no chasing docs, no lien risk, no waiting.</p>
                <div className="grid sm:grid-cols-3 gap-3 mt-7 text-left">
                  {[
                    { icon: DollarSign, t: 'Get paid faster', d: 'Submit a complete draw in one tap' },
                    { icon: ShieldCheck, t: 'Zero lien risk', d: 'Waivers & COIs collected for you' },
                    { icon: Clock, t: 'No chasing docs', d: 'Compliance verified before you send' },
                  ].map((p) => { const I = p.icon; return (
                    <div key={p.t} className="rounded-xl border border-slate-200 p-4">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><I className="w-5 h-5 text-blue-600" /></div>
                      <div className="font-semibold text-slate-800 mt-2">{p.t}</div>
                      <div className="text-xs text-slate-500">{p.d}</div>
                    </div>
                  ); })}
                </div>
              </div>
            )}

            {key === 'persona' && (
              <div>
                <Header eyebrow="Who you are" title="What best describes you?" sub="We'll tailor your workflow and checklists to match." />
                <div className="grid sm:grid-cols-3 gap-3">
                  {[
                    { id: 'developer', icon: Building2, t: 'Builder / Developer' },
                    { id: 'gc', icon: HardHat, t: 'General Contractor' },
                    { id: 'owner', icon: User, t: 'Owner-Builder' },
                  ].map((p) => { const I = p.icon; const on = persona === p.id; return (
                    <button key={p.id} onClick={() => setPersona(p.id)} className={`rounded-xl border p-4 text-left transition-all ${on ? 'border-blue-400 ring-1 ring-blue-200 bg-blue-50/50' : 'border-slate-200 hover:bg-slate-50'}`}>
                      <I className={`w-6 h-6 ${on ? 'text-blue-600' : 'text-slate-400'}`} />
                      <div className="font-semibold text-slate-800 mt-2">{p.t}</div>
                    </button>
                  ); })}
                </div>
                <Why>You never dig through features that don't apply to you — your dashboard shows only what moves your draw forward.</Why>
              </div>
            )}

            {key === 'details' && (
              <div>
                <Header eyebrow="Your details" title="Your business info" sub="This is what prints on your bank's draw form." />
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><label className="text-xs font-semibold text-slate-600">Business name</label><input className={inputCls} defaultValue="Mods Enterprises" /></div>
                  <div><label className="text-xs font-semibold text-slate-600">Business email</label><input className={inputCls} defaultValue="mods@modsenterprises.com" /></div>
                  <div><label className="text-xs font-semibold text-slate-600">Phone</label><input className={inputCls} defaultValue="319-555-0142" /></div>
                  <div><label className="text-xs font-semibold text-slate-600">EIN / TIN</label><input className={inputCls} placeholder="XX-XXXXXXX" /></div>
                </div>
                <Why>Enter it once — every future draw request auto-fills onto your bank's exact form. No re-typing, no formatting mistakes.</Why>
              </div>
            )}

            {key === 'lenders' && (
              <div>
                <Header eyebrow="Your lenders" title="Add your bank(s)" sub="Add each bank you work with and where their draws should go." />
                <div className="space-y-2 mb-3">
                  {lenders.map((l, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Landmark className="w-5 h-5 text-slate-500" /></div>
                      <div className="min-w-0 flex-1"><div className="font-semibold text-slate-800 truncate">{l.name}</div><div className="text-xs text-slate-500 truncate flex items-center gap-1"><Mail className="w-3 h-3" /> {l.email || 'no loan-dept email yet'}</div></div>
                      <span className="text-xs text-emerald-600 font-medium">✓ ready</span>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                  <input className={inputCls} style={{ marginTop: 0 }} placeholder="Lender name (e.g. Hills Bank)" value={lName} onChange={(e) => setLName(e.target.value)} />
                  <div>
                    <label className="text-xs font-semibold text-blue-700 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Construction loan department email</label>
                    <input className={inputCls} placeholder="constructiondept@yourbank.com" value={lEmail} onChange={(e) => setLEmail(e.target.value)} />
                  </div>
                  <button onClick={() => { if (lName.trim()) { setLenders((ls) => [...ls, { name: lName.trim(), email: lEmail.trim() }]); setLName(''); setLEmail(''); } }} className="inline-flex items-center gap-1.5 text-sm font-bold text-white rounded-lg px-4 py-2" style={{ background: NAVY }}><Plus className="w-4 h-4" /> Add lender</button>
                </div>
                <Why>Set the loan-department email <span className="font-semibold">here, once</span> — then a finished draw goes to your banker in one tap, straight from DrawReady (replies come back to you).</Why>
              </div>
            )}

            {key === 'contractors' && (
              <div>
                <Header eyebrow="Your crew" title="Add your subs & suppliers" sub="So DrawReady can collect their paperwork automatically." />
                <div className="space-y-2 mb-3">
                  {contractors.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Users className="w-5 h-5 text-slate-500" /></div>
                      <div className="min-w-0 flex-1"><div className="font-semibold text-slate-800 truncate">{c.company}</div><div className="text-xs text-slate-400 truncate">{c.email}</div></div>
                    </div>
                  ))}
                </div>
                <button onClick={() => setContractors((c) => [...c, { company: 'New Subcontractor', email: 'sub@email.com' }])} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg px-4 py-2"><Plus className="w-4 h-4" /> Add contractor</button>
                <Why>DrawReady auto-requests each sub's lien waiver and W-9 — so no unpaid sub can ever lien the bank's collateral, and you're never chasing signatures.</Why>
              </div>
            )}

            {key === 'compliance' && (
              <div>
                <Header eyebrow="Compliance" title="W-9 & Certificate of Insurance" sub="The two docs banks always ask for — handled up front." />
                <div className="grid sm:grid-cols-2 gap-3">
                  {[{ icon: FileCheck2, t: 'W-9', d: 'Collected once, reused every draw' }, { icon: ShieldCheck, t: 'Certificate of Insurance', d: 'Tracked so coverage never lapses' }].map((d) => { const I = d.icon; return (
                    <div key={d.t} className="rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center gap-2"><I className="w-5 h-5 text-emerald-600" /><span className="font-semibold text-slate-800">{d.t}</span></div>
                      <p className="text-xs text-slate-500 mt-1">{d.d}</p>
                      <button className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5"><Upload className="w-4 h-4" /> Upload</button>
                    </div>
                  ); })}
                </div>
                <Why>Lapsed insurance and missing W-9s are the quiet deal-killers on a draw. DrawReady flags them before the bank ever sees the request.</Why>
              </div>
            )}

            {key === 'done' && (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-emerald-600" /></div>
                <h1 className="mt-5 text-3xl font-extrabold text-slate-900">You're set — your first draw will be painless.</h1>
                <p className="mt-2 text-slate-600">Everything's in place: your bank, your crew, and your compliance. Start a project and DrawReady handles the headache.</p>
                <div className="mt-6 grid grid-cols-3 gap-3 text-left">
                  <div className="rounded-xl border border-slate-200 p-4"><Building2 className="w-5 h-5 text-blue-600" /><div className="text-sm font-semibold text-slate-800 mt-2">{lenders.length} lender{lenders.length === 1 ? '' : 's'}</div></div>
                  <div className="rounded-xl border border-slate-200 p-4"><Users className="w-5 h-5 text-blue-600" /><div className="text-sm font-semibold text-slate-800 mt-2">{contractors.length} contractor{contractors.length === 1 ? '' : 's'}</div></div>
                  <div className="rounded-xl border border-slate-200 p-4"><ShieldCheck className="w-5 h-5 text-emerald-600" /><div className="text-sm font-semibold text-slate-800 mt-2">Compliance ready</div></div>
                </div>
                <button className="mt-7 inline-flex items-center gap-2 rounded-full text-white px-6 py-3 text-base font-semibold" style={{ background: BLUE }}><Rocket className="w-5 h-5" /> Start your first draw</button>
              </div>
            )}
          </div>

          {/* nav */}
          {key !== 'done' && (
            <div className="flex items-center justify-between mt-5">
              <button onClick={back} disabled={step === 0} className={`inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full ${step === 0 ? 'text-slate-300' : 'text-slate-600 hover:bg-slate-200'}`}><ArrowLeft className="w-4 h-4" /> Back</button>
              <button onClick={next} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-6 py-2.5 rounded-full" style={{ background: BLUE }}>{step === 0 ? "Let's remove the headache" : 'Continue'} <ArrowRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
